/**
 * Status utilities for consistent status handling across the application
 */

/**
 * Get connection status configuration
 * @param {boolean} isLoading - Whether data is loading
 * @param {boolean} isConnected - Whether connected to data source
 * @param {boolean} hasData - Whether there is data available
 * @param {boolean} hasError - Whether there is an error
 * @returns {Object} Status configuration with color, text, and animation
 */
export const getConnectionStatus = (isLoading, isConnected, hasData, hasError) => {
  if (isLoading) {
    return { 
      status: 'loading', 
      text: 'Connecting...', 
      animation: 'pulse', 
    }
  }
  
  if (isConnected && hasData) {
    return { 
      status: 'success', 
      text: 'Connected', 
      animation: 'glow', 
    }
  }
  
  if (hasError) {
    return { 
      status: 'error', 
      text: 'Connection Error', 
      animation: 'none', 
    }
  }
  
  return { 
    status: 'info', 
    text: 'No Data', 
    animation: 'none', 
  }
}

/**
 * Get configuration status configuration
 * @param {boolean} hasErrors - Whether there are validation errors
 * @param {number} errorCount - Number of errors
 * @param {boolean} isDirty - Whether form has unsaved changes
 * @param {boolean} isSaving - Whether currently saving
 * @param {boolean} hasSavedData - Whether there is saved data
 * @returns {Object} Status configuration
 */
export const getConfigurationStatus = (hasErrors, errorCount, isDirty, isSaving, hasSavedData) => {
  if (hasErrors && errorCount > 0) {
    return {
      status: 'error',
      text: `${errorCount} Error${errorCount !== 1 ? 's' : ''}`,
      animation: 'none',
    }
  }
  
  if (isSaving) {
    return {
      status: 'loading',
      text: 'Saving...',
      animation: 'pulse',
    }
  }
  
  if (isDirty) {
    return {
      status: 'warning',
      text: 'Unsaved Changes',
      animation: 'none',
    }
  }
  
  if (hasSavedData) {
    return {
      status: 'success',
      text: 'Configuration Saved',
      animation: 'none',
    }
  }
  
  return {
    status: 'info',
    text: 'Ready',
    animation: 'none',
  }
}

/**
 * Common status types for consistency
 */
export const STATUS_TYPES = {
  SUCCESS: 'success',
  WARNING: 'warning', 
  ERROR: 'error',
  LOADING: 'loading',
  INFO: 'info',
}

/**
 * Common animation types
 */
export const ANIMATION_TYPES = {
  PULSE: 'pulse',
  GLOW: 'glow',
  NONE: 'none',
}
